import { defineStore, getActivePinia, storeToRefs, type Pinia } from 'pinia';
import { computed, hasInjectionContext, inject, onScopeDispose, provide, ref, toRaw, watch, type InjectionKey } from 'vue';
import type {
  SheetCellStyle,
  SheetColumn,
  SheetDetail,
  SheetFilter,
  SheetRow,
  SheetRowPositionChange,
  SheetRowData,
  SheetViewConfig,
} from '@yana/shared';
import { BASE_AGE_LAYOUT_VERSION, BASE_PHONE_LAYOUT_VERSION, BASE_ROW_NUMBER_LAYOUT_VERSION, migrateBaseAgeView, migrateBasePhoneView, migrateBaseRowNumberView, reconcileSheetRows, sameSheetRow, sheetCompletionLabel, sheetRawValue, sheetRowsInPositionOrder, todayIso } from '@yana/shared';
import { ApiError } from '@/api/client';
import { sheetsApi, type SheetCellsResponse } from '@/api/sheets';
import { markError, markSaved, markSaving } from '@/lib/cellDecorations';
import { useToastStore } from '@/stores/toast';
import { useAuthStore } from '@/stores/auth';
import { useSheetCatalogStore } from './sheetCatalog';
import { createTableFreshness } from '@/lib/tableFreshness';
import { allowedSheetObservationView, type SheetObservationView } from '@/lib/sheetObservation';
import { sheetUndoContext, useUndoStack, type UndoRecorder } from '@/composables/useUndoStack';

/**
 * Состояние одного листа. Каждый код получает собственный экземпляр Pinia.
 *
 * Отдельно от стора основной таблицы: там колонки известны на сборке,
 * фильтры типизированы по кандидату, есть корзина, группы выезда и
 * realtime по кандидатам. Здесь всё описание приходит с сервера, и общего
 * между ними — только слово «таблица».
 */

/** Раскладка колонок под конкретного человека — в браузере, как у грида. */
const layoutKey = (code: string) => `yana.sheet.${code}.layout`;

interface Layout {
  rotationStatusLayoutVersion?: 1;
  basePhoneLayoutVersion?: typeof BASE_PHONE_LAYOUT_VERSION;
  baseRowNumberLayoutVersion?: typeof BASE_ROW_NUMBER_LAYOUT_VERSION;
  baseAgeLayoutVersion?: typeof BASE_AGE_LAYOUT_VERSION;
  columns: string[];
  widths: Record<string, number>;
  pinned?: Record<string, 'left' | null>;
}

function readLayout(code: string): Layout | null {
  try {
    const raw = localStorage.getItem(layoutKey(code));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Layout;
    if (!Array.isArray(parsed.columns)) return null;
    let migrated = parsed;
    if (code === 'base') migrated = migrateBaseAgeView(migrateBaseRowNumberView(migrateBasePhoneView({ ...parsed })));
    if (code === 'rotation' && parsed.rotationStatusLayoutVersion !== 1) {
      const columns = [...parsed.columns];
      if (!columns.includes('status')) {
        const objectIndex = columns.indexOf('objectId');
        columns.splice(objectIndex < 0 ? columns.length : objectIndex, 0, 'status');
      }
      migrated = { ...parsed, columns, rotationStatusLayoutVersion: 1 };
    }
    if (JSON.stringify(migrated) !== JSON.stringify(parsed)) {
      try { localStorage.setItem(layoutKey(code), JSON.stringify(migrated)); } catch {
        // Новая раскладка работает и при заполненном/закрытом хранилище.
      }
    }
    return migrated;
  } catch {
    return null;
  }
}

/** Фильтры ссылок, сотрудников и завершённых вахт сравнивают подписи, остальных колонок — значения. */
function columnFilterValue(row: SheetRow, column: SheetColumn): unknown {
  if (column.type === 'ref' || column.type === 'user') {
    return row.labels[column.key] || (row.data[column.key] ?? null);
  }
  const completionLabel = sheetCompletionLabel(row, column);
  if (completionLabel) return completionLabel;
  return sheetRawValue(row, column);
}

function createSheetState(boundCode?: string) {
  const toasts = useToastStore();
  const auth = useAuthStore();

  const catalog = useSheetCatalogStore();
  const { sheets } = storeToRefs(catalog);
  const loadList = catalog.loadList;

  const detail = ref<SheetDetail | null>(null);
  const rows = ref<SheetRow[]>([]);
  const freshness = createTableFreshness();
  const invalidateRows = freshness.invalidate;
  /** Описание нового листа приходит раньше его строк. */
  const rowsCode = ref('');
  /** Меняется только после применённого GET, а не после вытесненного или отложенного чтения. */
  const rowsRevision = ref(0);
  const total = ref(0);
  /** Сегодня по серверу: заливка по датам считается им, а не часами браузера */
  const today = ref(todayIso());
  const loading = ref(false);
  const refreshing = ref(false);
  const rowsQuery = ref<string | null>(null);
  const refreshError = ref<string | null>(null);
  const error = ref<string | null>(null);

  const search = ref('');
  /**
   * Фильтр по значениям колонки — воронка в заголовке.
   *
   * Считается на клиенте, а не на сервере, хотя сервер это умеет.
   * Причина в самом списке значений: собрать его можно только из строк
   * БЕЗ этого фильтра, иначе список схлопывается до уже выбранного —
   * снял галочку и больше её не вернёшь. Строки листа и так приходят
   * целиком (самый большой — 1440), так что считать не на чем.
   */
  const columnFilters = ref<Record<string, string[]>>({});
  const activeViewId = ref<string | null>(null);
  const sort = ref<{ field: string; dir: 'asc' | 'desc' }[]>([]);
  const visibleKeys = ref<string[] | null>(null);
  const widths = ref<Record<string, number>>({});
  const personalPins = ref<Record<string, 'left' | null>>({});
  // Effective filters may differ from a saved view after a local adjustment,
  // or belong to a colleague's private view while observing.
  const observationFilters = ref<SheetFilter[] | null>(null);
  const temporaryObservationLayout = ref(false);
  const observationActive = ref(false);
  let openSequence = 0;
  let reloadSequence = 0;
  let mutationSequence = 0;
  let dataWriteSequence = 0;
  const latestDataWrites = new Map<string, number>();
  const latestPositionWrites = new Map<string, number>();
  const positionQueues = new Map<string, Promise<void>>();
  const sessionSequence = ref(0);
  type DataWriteContext = { code: string; opening: number; session: number };
  const pendingDataWrites = new Map<number, {
    context: DataWriteContext;
    rowIds: string[];
    invalidate: () => void;
    timer: ReturnType<typeof setTimeout>;
  }>();
  const DATA_WRITE_TTL_MS = 2 * 60_000;
  let deferredDataReload: DataWriteContext | null = null;
  let deferredPositionDetail: DataWriteContext | null = null;
  let entryRequest: { code: string; opening: number; promise: Promise<void> } | null = null;
  type RowPosition = Pick<SheetRow, 'sectionId' | 'sortOrder'>;
  const positionWrites = new Map<number, {
    context: DataWriteContext;
    before: Map<string, RowPosition>;
    changes: Map<string, SheetRowPositionChange>;
  }>();

  const code = computed(() => detail.value?.sheet.code ?? '');
  const columns = computed(() =>
    (detail.value?.columns ?? []).map((column) =>
      column.key in personalPins.value
        ? { ...column, pinned: personalPins.value[column.key] ?? null }
        : column,
    ),
  );
  const columnByKey = computed(() => new Map(columns.value.map((column) => [column.key, column])));
  const canEdit = computed(() => !observationActive.value && (detail.value?.canEdit ?? false));
  const canManage = computed(() => !observationActive.value && (detail.value?.canManage ?? false));

  const activeView = computed(
    () => detail.value?.views.find((v) => v.id === activeViewId.value) ?? null,
  );
  const activeFilterCount = computed(
    () => (observationFilters.value ?? activeView.value?.config.filters ?? []).length,
  );

  /** Колонки, которые видно: из вида, из личной раскладки или по умолчанию. */
  const visibleColumns = computed<SheetColumn[]>(() => {
    const all = columns.value;
    const keys = visibleKeys.value;
    const selectedKeys = keys ?? all.filter((c) => c.visibleByDefault).map((c) => c.key);

    // Порядок — как в раскладке, но закреплённые всегда слева: иначе
    // закреплённая колонка уезжает в середину и «прилипает» посреди таблицы
    const chosen = selectedKeys
      .map((key) => columnByKey.value.get(key))
      .filter((c): c is SheetColumn => !!c);
    return [
      ...chosen.filter((c) => c.pinned === 'left'),
      ...chosen.filter((c) => c.pinned !== 'left'),
    ];
  });

  // Колонки и выбранные значения разрешаются один раз на смену фильтра,
  // а не линейным поиском для каждой из двенадцати тысяч строк.
  const preparedColumnFilters = computed(() =>
    Object.entries(columnFilters.value).flatMap(([key, values]) => {
      const column = columnByKey.value.get(key);
      return column && values.length ? [{ key, column, values: new Set(values) }] : [];
    }),
  );

  /** Строки после фильтров по колонкам — то, что видно в таблице. */
  const visibleRows = computed<SheetRow[]>(() => {
    const active = preparedColumnFilters.value;
    if (!active.length) return rows.value;

    return rows.value.filter((row) =>
      active.every(({ column, values }) => matchesColumnFilter(row, column, values)),
    );
  });

  function matchesColumnFilter(row: SheetRow, column: SheetColumn, values: ReadonlySet<string>): boolean {
    const value = columnFilterValue(row, column);
    // Сохранённые ранее виды могли выбирать конкретное отрицательное число.
    if (sheetCompletionLabel(row, column) && values.has(String(sheetRawValue(row, column)))) return true;
    // Пустая ячейка — такое же значение, как остальные: в книге
    // «не заполнено» ищут не реже, чем конкретный объект
    if (Array.isArray(value)) return value.some((v) => values.has(String(v)));
    return values.has(value === null || value === undefined ? '' : String(value));
  }

  /** Все значения колонки — из строк без учёта её собственного фильтра. */
  function distinctValues(key: string): string[] {
    const column = columnByKey.value.get(key);
    if (!column) return [];

    const others = preparedColumnFilters.value.filter((filter) => filter.key !== key);

    const seen = new Set<string>();
    for (const row of rows.value) {
      const passes = others.every(({ column, values }) => matchesColumnFilter(row, column, values));
      if (!passes) continue;

      const value = columnFilterValue(row, column);

      if (Array.isArray(value)) value.forEach((v) => seen.add(String(v)));
      else seen.add(value === null || value === undefined ? '' : String(value));
    }

    return [...seen].sort((a, b) => {
      // Пустое — в конец: его ищут реже, чем значения
      if (!a) return 1;
      if (!b) return -1;
      return a.localeCompare(b, 'ru');
    });
  }

  async function setColumnFilter(key: string, values: string[] | null): Promise<void> {
    const next = { ...columnFilters.value };
    if (!values || !values.length) delete next[key];
    else next[key] = values;
    columnFilters.value = next;
    // A preset's `in` filter was also applied on the server. Keeping it there
    // would make clearing or widening the visible column filter ineffective.
    const current = observationFilters.value ?? activeView.value?.config.filters ?? [];
    const remaining = current.filter((filter) => filter.field !== key || filter.op !== 'in');
    if (remaining.length !== current.length) {
      observationFilters.value = remaining;
      await reload();
    }
  }

  function clearColumnFilters(): void {
    columnFilters.value = {};
  }

  function widthOf(column: SheetColumn): number {
    return widths.value[column.key] ?? column.width;
  }

  watch(() => catalog.revision, () => {
    const sheetCode = boundCode ?? code.value;
    if (catalog.loaded && sheetCode && !sheets.value.some(sheet => sheet.code === sheetCode)) {
      closeUnavailableSheet(sheetCode === 'base'
        ? 'Доступ к «Базе» выключен администратором'
        : 'Лист удалён или доступ к нему закрыт');
    }
  }, { flush: 'sync' });

  /** Отзыв общего доступа убирает уже загруженные данные и отменяет устаревшие ответы. */
  function closeUnavailableSheet(message: string): void {
    invalidateRows();
    clearPendingDataWrites();
    latestDataWrites.clear();
    openSequence += 1;
    reloadSequence += 1;
    detail.value = null;
    rows.value = [];
    rowsCode.value = '';
    total.value = 0;
    loading.value = false;
    refreshing.value = false;
    refreshError.value = null;
    error.value = message;
  }

  function applyView(config: SheetViewConfig | null): void {
    if (!config) return;
    if (code.value === 'base') config = migrateBaseAgeView(migrateBaseRowNumberView(config));
    if (config.columns.length) visibleKeys.value = [...config.columns];
    if (config.sort?.length) sort.value = config.sort.map((s) => ({ field: s.field, dir: s.dir }));
    // Фильтры вида по значениям колонки возвращаются в воронки заголовков
    for (const filter of config.filters ?? []) {
      if (filter.op === 'in' && Array.isArray(filter.value)) {
        columnFilters.value = {
          ...columnFilters.value,
          [filter.field]: filter.value.map(String),
        };
      }
    }
    if (config.search !== undefined) search.value = config.search ?? '';
    widths.value = { ...config.widths };
  }

  function hasSnapshot(sheetCode: string): boolean {
    return code.value === sheetCode && rowsCode.value === sheetCode && !error.value
      && !observationActive.value && !temporaryObservationLayout.value;
  }

  function hasLoadedRows(sheetCode: string): boolean {
    return hasSnapshot(sheetCode) && rowsQuery.value === JSON.stringify(rowQuery());
  }

  function rowQuery() {
    return {
      search: search.value || undefined,
      filters: observationFilters.value ?? activeView.value?.config.filters ?? [],
      sort: sort.value,
      mode: 'all' as const,
    };
  }

  /** Возврат на любую вкладку сохраняет её состояние; сеть не закрывает таблицу. */
  function enter(sheetCode: string): Promise<void> {
    assertSheetCode(sheetCode);
    if (entryRequest?.code === sheetCode && entryRequest.opening === openSequence) return entryRequest.promise;
    if (hasLoadedRows(sheetCode) && freshness.isFresh()) return Promise.resolve();
    const promise = hasSnapshot(sheetCode) ? refreshOnReturn() : open(sheetCode);
    entryRequest = { code: sheetCode, opening: openSequence, promise };
    void promise.finally(() => { if (entryRequest?.promise === promise) entryRequest = null; });
    return promise;
  }

  async function refreshOnReturn(): Promise<void> {
    const sheetCode = code.value;
    const opening = openSequence;
    try {
      await refreshDetail();
      if (opening !== openSequence || code.value !== sheetCode) return;
      await reload({ background: true });
    } catch (e) {
      if (opening !== openSequence || code.value !== sheetCode) return;
      const message = e instanceof ApiError ? e.message : 'Не удалось обновить таблицу';
      if (e instanceof ApiError && [401, 403, 404].includes(e.status)) closeUnavailableSheet(message);
      else {
        refreshError.value = message;
        toasts.error(message);
      }
    }
  }

  async function open(sheetCode: string): Promise<void> {
    assertSheetCode(sheetCode);
    clearPendingDataWrites();
    const request = ++openSequence;
    reloadSequence += 1;
    loading.value = true;
    error.value = null;
    refreshError.value = null;
    try {
      const loaded = await sheetsApi.detail(sheetCode);
      if (request !== openSequence) return;
      detail.value = loaded;
      observationFilters.value = null;
      temporaryObservationLayout.value = false;
      personalPins.value = {};

      const layout = readLayout(sheetCode);
      const defaultView =
        detail.value.views.find((v) => v.isDefault) ?? detail.value.views[0] ?? null;

      activeViewId.value = defaultView?.id ?? null;
      columnFilters.value = {};
      visibleKeys.value = null;
      sort.value = [];
      search.value = '';
      widths.value = {};

      applyView(defaultView?.config ?? null);
      // Личная раскладка сильнее вида: человек перетаскивал колонки под себя
      if (layout) {
        visibleKeys.value = layout.columns.filter((key) =>
          detail.value!.columns.some((c) => c.key === key),
        );
        widths.value = { ...widths.value, ...layout.widths };
        personalPins.value = layout.pinned ?? {};
      }

      await reload();
    } catch (e) {
      if (request !== openSequence) return;
      error.value = e instanceof ApiError ? e.message : 'Лист не открылся';
      detail.value = null;
      rows.value = [];
      rowsCode.value = '';
    } finally {
      if (request === openSequence) loading.value = false;
    }
  }

  function assertSheetCode(sheetCode: string): void {
    if (boundCode !== undefined && sheetCode !== boundCode) {
      throw new Error(`Стор листа ${boundCode} не может открыть ${sheetCode}`);
    }
  }

  function currentDataWriteContext(context: DataWriteContext): boolean {
    return code.value === context.code && openSequence === context.opening
      && sessionSequence.value === context.session;
  }

  function dataWritePending(): boolean {
    return [...pendingDataWrites.values()].some(write => currentDataWriteContext(write.context));
  }

  function resumeDeferredDataReload(): void {
    const context = deferredDataReload;
    if (!context) return;
    if (!currentDataWriteContext(context)) {
      deferredDataReload = null;
      return;
    }
    if (dataWritePending()) return;
    deferredDataReload = null;
    if (deferredPositionDetail && currentDataWriteContext(deferredPositionDetail)) {
      const positionContext = deferredPositionDetail;
      deferredPositionDetail = null;
      // Перенос меняет счётчики секций, а не колонки/виды. Их замена
      // свежими одинаковыми объектами сбросила бы кеш высот всего листа.
      if (detail.value?.sections.length) {
        const mutation = mutationSequence;
        void sheetsApi.detail(positionContext.code).then(fresh => {
          if (!currentDataWriteContext(positionContext) || mutation !== mutationSequence || !detail.value) return;
          const counts = new Map(fresh.sections.map(section => [section.id, section.rowCount]));
          detail.value.sections.forEach(section => {
            const count = counts.get(section.id);
            if (count !== undefined && section.rowCount !== count) section.rowCount = count;
          });
        }).catch(() => {});
      }
    }
    void reload({ background: true });
  }

  function clearPendingDataWrites(): void {
    deferredDataReload = null;
    deferredPositionDetail = null;
    for (const write of pendingDataWrites.values()) {
      clearTimeout(write.timer);
      write.invalidate();
    }
    pendingDataWrites.clear();
  }

  function trackPendingDataWrite(write: number, context: DataWriteContext, rowIds: string[],
    invalidate = () => rowIds.forEach(id => { if (latestDataWrites.get(id) === write) latestDataWrites.delete(id); }),
  ): () => void {
    invalidateRows();
    const release = () => {
      const pending = pendingDataWrites.get(write);
      if (!pending) return;
      clearTimeout(pending.timer);
      pendingDataWrites.delete(write);
      resumeDeferredDataReload();
    };
    // Потерянный HTTP-ответ не может держать снимки GET заблокированными
    // бесконечно. После того же срока, что у WS-операции, читаем сервер.
    const timer = setTimeout(() => {
      // Просроченный HTTP-ответ уже не авторитетнее завершающего GET,
      // даже когда updatedAt отсутствует или совпал с другим снимком.
      invalidate();
      release();
    }, DATA_WRITE_TTL_MS);
    pendingDataWrites.set(write, { context, rowIds: [...rowIds], timer, invalidate });
    return release;
  }

  async function reload(options: { background?: boolean } = {}): Promise<void> {
    if (!detail.value) return;
    const freshnessRead = freshness.beginRead();
    const request = ++reloadSequence;
    const sheetCode = detail.value.sheet.code;
    const mutationAtStart = mutationSequence;
    if (!options.background) loading.value = true;
    refreshing.value = true;
    try {
      const query = rowQuery();
      const queryKey = JSON.stringify(query);
      // Сервер возвращает всю выборку, в том числе строки после 20 000.
      // Виртуализация ограничивает DOM, а не доступные пользователю данные.
      const result = await sheetsApi.rows(sheetCode, query);
      if (request !== reloadSequence || code.value !== sheetCode) return;
      // GET мог начаться уже после оптимистичного ввода, пока PATCH ещё
      // не записал его. Такой снимок не заменяет черновик. После последней
      // ожидающей записи перечитаем всю актуальную выборку, включая чужие правки.
      if (dataWritePending()) {
        deferredDataReload = { code: sheetCode, opening: openSequence, session: sessionSequence.value };
        return;
      }
      // GET мог начаться до новой правки. Старый снимок не должен стирать
      // уже показанное значение; завершающее чтение забирает актуальную выборку.
      if (mutationAtStart !== mutationSequence) return await reload({ background: true });
      // Сравнение HTTP-снимков не собирает реактивных зависимостей для каждой ячейки.
      rows.value = reconcileSheetRows(toRaw(rows.value), result.rows);
      rowsCode.value = sheetCode;
      rowsQuery.value = queryKey;
      rowsRevision.value += 1;
      total.value = result.total;
      today.value = result.today;
      /*
        Удачное перечитывание снимает прежнюю ошибку.

        Без этой строки одна неудачная загрузка — оборвалась связь на щелчке
        по сортировке — навсегда подменяла таблицу карточкой «Лист не открылся»:
        строки лежали в памяти, поиск и правка работали, а на экране была
        заглушка, и вернуть таблицу можно было только уходом на другой лист.
      */
      error.value = null;
      refreshError.value = null;
      freshness.markFresh(freshnessRead);
    } catch (e) {
      if (request !== reloadSequence || code.value !== sheetCode) return;
      const message = e instanceof ApiError ? e.message : 'Данные листа не загрузились';
      if (e instanceof ApiError && [401, 403, 404].includes(e.status)) {
        closeUnavailableSheet(message);
        return;
      }
      if (options.background && !(e instanceof ApiError && [401, 403, 404].includes(e.status))) {
        refreshError.value = message;
        toasts.error(message);
      } else error.value = message;
    } finally {
      if (request === reloadSequence) {
        loading.value = false;
        refreshing.value = false;
      }
    }
  }

  async function selectView(viewId: string | null): Promise<void> {
    observationFilters.value = null;
    activeViewId.value = viewId;
    const view = detail.value?.views.find((v) => v.id === viewId) ?? null;
    /*
      Переключение вида сбрасывает раскладку целиком, а не подмешивается
      к прежней. На основной таблице это уже было ошибкой: срез «врал» —
      человек переключался, а фильтр от предыдущего оставался.
    */
    visibleKeys.value = null;
    widths.value = {};
    sort.value = [];
    columnFilters.value = {};
    search.value = '';
    applyView(view?.config ?? null);
    await reload();
  }

  /** Снять условия выборки, сохранив выбранные колонки, ширины и сортировку. */
  async function resetFilters(): Promise<void> {
    search.value = '';
    columnFilters.value = {};
    activeViewId.value = null;
    observationFilters.value = null;
    await reload();
  }

  function captureObservationView(): SheetObservationView {
    return {
      activeViewId: activeViewId.value,
      search: search.value,
      sort: sort.value.map((item) => ({ ...item })),
      filters: JSON.parse(
        JSON.stringify(observationFilters.value ?? activeView.value?.config.filters ?? []),
      ),
      columnFilters: Object.fromEntries(
        Object.entries(columnFilters.value).map(([key, values]) => [key, [...values]]),
      ),
      columns: visibleColumns.value.map((column) => column.key),
      widths: Object.fromEntries(columns.value.map((column) => [column.key, widthOf(column)])),
      pins: Object.fromEntries(columns.value.map((column) => [column.key, column.pinned])),
    };
  }

  /** A changed login must discard previous data instead of restoring its saved observation view. */
  function discardObservation(): void {
    invalidateRows();
    entryRequest = null;
    rowsQuery.value = null;
    clearPendingDataWrites();
    latestDataWrites.clear();
    openSequence += 1;
    reloadSequence += 1;
    sessionSequence.value += 1;
    observationFilters.value = null;
    temporaryObservationLayout.value = false;
    observationActive.value = false;
    detail.value = null;
    rows.value = [];
    rowsCode.value = '';
    total.value = 0;
    loading.value = false;
    refreshing.value = false;
    refreshError.value = null;
    error.value = null;
    search.value = '';
    sort.value = [];
    activeViewId.value = null;
    columnFilters.value = {};
    visibleKeys.value = null;
    widths.value = {};
    personalPins.value = {};
  }

  watch([() => auth.user?.id, () => auth.user?.role],
    () => discardObservation(), { flush: 'sync' });
  onScopeDispose(discardObservation);

  /** Apply only local display state; never call layout persistence or write endpoints. */
  async function applyObservationView(input: unknown, temporary = true): Promise<boolean> {
    const view = allowedSheetObservationView(
      input,
      columns.value.map((column) => column.key),
    );
    if (!view) return false;
    const previousQuery = JSON.stringify({
      search: search.value,
      sort: sort.value,
      filters: observationFilters.value ?? activeView.value?.config.filters ?? [],
    });
    activeViewId.value = view.activeViewId;
    search.value = view.search;
    sort.value = view.sort;
    // Restore local adjustments too: the saved preset may still contain a
    // filter the user removed before beginning observation.
    observationFilters.value = view.filters;
    temporaryObservationLayout.value = temporary;
    columnFilters.value = view.columnFilters;
    visibleKeys.value = view.columns;
    widths.value = view.widths;
    personalPins.value = view.pins;
    if (
      previousQuery !==
      JSON.stringify({
        search: view.search,
        sort: view.sort,
        filters: view.filters,
      })
    ) {
      await reload();
    }
    return true;
  }

  /** Полный ответ PATCH заменяет своё WS-эхо только в простой локальной выборке. */
  function flatDataQuery(): boolean {
    return detail.value?.sheet.kind === 'custom' && !detail.value.sheet.sourceTable
      && !search.value.trim() && !sort.value.length
      && !(observationFilters.value ?? activeView.value?.config.filters ?? []).length
      && columns.value.every(column => column.type !== 'ref' && column.type !== 'user'
        && (column.type !== 'computed' || column.rule?.kind === 'rowNumber'));
  }

  function dataResultOptions(sheetCode: string, rowIds: string[]) {
    const write = ++dataWriteSequence;
    rowIds.forEach(id => latestDataWrites.set(id, write));
    const opening = openSequence;
    const session = sessionSequence.value;
    const releasePending = trackPendingDataWrite(write, { code: sheetCode, opening, session }, rowIds);
    const eligible = flatDataQuery();
    const expectedIds = new Set(rowIds);
    let expected: SheetRow[] | null = null;
    let handled = false;
    let dateChanged = false;
    const currentContext = () => code.value === sheetCode && opening === openSequence && session === sessionSequence.value;
    const onResult = (result: SheetCellsResponse) => {
      handled = true;
      if (!currentContext()) return;
      // Ответ старой правки не должен затереть более позднее сохранение.
      if (!rowIds.some(id => latestDataWrites.get(id) === write)) return;
      mutationSequence += 1;
      const currentRows = new Map(rows.value.map(row => [row.id, row]));
      const accepted = result.rows.filter(row => {
        if (latestDataWrites.get(row.id) !== write) return false;
        const current = currentRows.get(row.id);
        return !row.updatedAt || !current?.updatedAt || row.updatedAt >= current.updatedAt;
      });
      const positionPending = [...positionWrites.values()].some(write => currentDataWriteContext(write.context));
      const numberKeys = columns.value.filter(column => column.type === 'computed'
        && column.rule?.kind === 'rowNumber').map(column => column.key);
      applyFresh(accepted.map(row => {
        const current = currentRows.get(row.id);
        if (!current || !latestPositionWrites.has(row.id)) return row;
        return { ...row, sectionId: current.sectionId, sortOrder: current.sortOrder,
          ...(current.position !== undefined ? { position: current.position } : {}),
          computed: { ...row.computed, ...Object.fromEntries(numberKeys
            .filter(key => key in current.computed).map(key => [key, current.computed[key]!])) },
        };
      }));
      if (positionPending) orderPositionRows();
      dateChanged = today.value !== result.today;
      if (result.today >= today.value) today.value = result.today;
      expected = !positionPending && accepted.length === result.rows.length && result.rows.length === expectedIds.size
        && new Set(result.rows.map(row => row.id)).size === expectedIds.size
        && result.rows.every(row => expectedIds.has(row.id))
        ? JSON.parse(JSON.stringify(result.rows)) as SheetRow[] : null;
    };
    return {
      onResult,
      isApplied: () => {
        if (!eligible || dateChanged || error.value || refreshError.value
          || !currentContext() || !flatDataQuery() || !expected?.length) return false;
        const byId = new Map(rows.value.map(row => [row.id, row]));
        return expected.every(row => {
          const current = byId.get(row.id);
          return !!current && sameSheetRow(current, row);
        });
      },
      ensureResult: (result: SheetCellsResponse) => { if (!handled) onResult(result); },
      isCurrentWrite: (id: string) => currentContext() && latestDataWrites.get(id) === write,
      release: () => {
        rowIds.forEach(id => { if (latestDataWrites.get(id) === write) latestDataWrites.delete(id); });
        releasePending();
      },
    };
  }

  function orderPositionRows(): void {
    // При поиске или сортировке полный порядок задаёт последующий GET.
    if (search.value.trim() || sort.value.length || activeFilterCount.value || observationActive.value
      || total.value !== rows.value.length) return;
    rows.value = sheetRowsInPositionOrder(rows.value);
    const numberKeys = columns.value.filter(column => column.type === 'computed'
      && column.rule?.kind === 'rowNumber').map(column => column.key);
    rows.value.forEach((row, index) => {
      if (numberKeys.some(key => row.computed[key] !== index + 1)) {
        row.computed = { ...row.computed, ...Object.fromEntries(numberKeys.map(key => [key, index + 1])) };
      }
      if (row.position !== undefined) row.position = index + 1;
    });
    if (detail.value) {
      const counts = new Map<string, number>();
      rows.value.forEach(row => { if (row.sectionId) counts.set(row.sectionId, (counts.get(row.sectionId) ?? 0) + 1); });
      detail.value.sections.forEach(section => { section.rowCount = counts.get(section.id) ?? 0; });
    }
  }

  function rollbackPositionWrite(write: number): void {
    const record = positionWrites.get(write);
    if (!record || !currentDataWriteContext(record.context)) return;
    const currentRows = new Map(rows.value.map(row => [row.id, row]));
    for (const [id, patch] of record.changes) {
      const before = record.before.get(id);
      if (!before) continue;
      // Следующая оптимистичная операция могла опираться на неудавшийся
      // перенос. Её откат должен вернуть последний настоящий порядок.
      for (const [laterId, later] of positionWrites) {
        if (laterId <= write || later.context.code !== record.context.code
          || later.context.opening !== record.context.opening || later.context.session !== record.context.session) continue;
        const previous = later.before.get(id);
        if (!previous) continue;
        if (previous.sectionId === patch.sectionId) previous.sectionId = before.sectionId;
        if (previous.sortOrder === patch.sortOrder) previous.sortOrder = before.sortOrder;
      }
      const row = currentRows.get(id);
      if (!row || latestPositionWrites.get(id) !== write) continue;
      if (row.sectionId === patch.sectionId) row.sectionId = before.sectionId;
      if (row.sortOrder === patch.sortOrder) row.sortOrder = before.sortOrder;
    }
    mutationSequence += 1;
    orderPositionRows();
  }

  /** Перенос и его отмена меняют только позиции; HTTP-порядок записей последовательный. */
  async function applyRowPositions(sheetCode: string, changes: SheetRowPositionChange[]): Promise<void> {
    if (!changes.length) return;
    const session = sessionSequence.value;
    const context = { code: sheetCode, opening: openSequence, session };
    const active = code.value === sheetCode;
    if (active && (!canEdit.value || observationActive.value)) throw new Error('Нет права перемещать строки');
    const patches = new Map(changes.map(change => [change.rowId, { ...change }]));
    const write = ++dataWriteSequence;
    const before = new Map<string, RowPosition>();
    let expired = false;
    let releasePending = () => {};
    if (active) {
      const currentRows = new Map(rows.value.map(row => [row.id, row]));
      for (const [id, patch] of patches) {
        latestPositionWrites.set(id, write);
        const row = currentRows.get(id);
        if (!row) continue;
        before.set(id, { sectionId: row.sectionId, sortOrder: row.sortOrder });
        row.sectionId = patch.sectionId;
        row.sortOrder = patch.sortOrder;
      }
      mutationSequence += 1;
      positionWrites.set(write, { context, before, changes: patches });
      deferredDataReload = context;
      deferredPositionDetail = context;
      releasePending = trackPendingDataWrite(write, context, [...patches.keys()], () => {
        expired = true;
        patches.forEach((_patch, id) => { if (latestPositionWrites.get(id) === write) latestPositionWrites.delete(id); });
        positionWrites.delete(write);
      });
      orderPositionRows();
    }
    const previous = positionQueues.get(sheetCode) ?? Promise.resolve();
    const request = previous.then(async () => {
      if (session !== sessionSequence.value) throw new Error('Сессия изменилась до сохранения порядка');
      if (expired && currentDataWriteContext(context)) throw new Error('Срок ожидания переноса истёк');
      await sheetsApi.patchCells(sheetCode, [...patches.values()]);
    });
    const tail = request.catch(() => {});
    positionQueues.set(sheetCode, tail);
    try {
      await request;
    } catch (failure) {
      if (active) rollbackPositionWrite(write);
      throw failure;
    } finally {
      if (positionQueues.get(sheetCode) === tail) positionQueues.delete(sheetCode);
      if (code.value === sheetCode && session === sessionSequence.value) {
        const current = { code: sheetCode, opening: openSequence, session };
        deferredDataReload = current;
        deferredPositionDetail = current;
      }
      patches.forEach((_patch, id) => { if (latestPositionWrites.get(id) === write) latestPositionWrites.delete(id); });
      positionWrites.delete(write);
      releasePending();
      resumeDeferredDataReload();
    }
  }

  /** Правка одной ячейки. */
  async function setValue(
    rowId: string,
    key: string,
    value: unknown,
    strict = false,
  ): Promise<boolean> {
    if (observationActive.value) return false;
    if (!detail.value) return false;
    const sheetCode = code.value;
    const row = rows.value.find((r) => r.id === rowId);
    if (!row) {
      if (!strict) return false;
      mutationSequence += 1;
      const options = dataResultOptions(sheetCode, [rowId]);
      try {
        const result = await sheetsApi.patchCells(sheetCode, [{ rowId, data: { [key]: value } }], options);
        options.ensureResult(result);
      } finally { options.release(); }
      return true;
    }

    const before = row.data[key] ?? null;
    if (JSON.stringify(before) === JSON.stringify(value ?? null)) return false;
    mutationSequence += 1;

    // Значение показывается сразу, до ответа сервера: иначе на каждой правке
    // ячейка на мгновение возвращается к прежнему тексту
    row.data = { ...row.data, [key]: value };
    /*
      Состояние правки ставится классом прямо на элемент, мимо реактивности.

      В основной таблице это уже выстрадано: пока «сохраняется / сохранено»
      лежало в сторе, правка ОДНОЙ ячейки заставляла Vue пересчитать классы
      у всех видимых — а их под тысячу, и трижды подряд на одну правку.
      Отсюда и мигало всё разом. Здесь та же беда была бы ровно такой же.
    */
    markSaving(rowId, key);
    const options = dataResultOptions(sheetCode, [rowId]);

    try {
      /*
        Вычисляемые колонки считает сервер: после правки даты выезда должна
        измениться и дата окончания. Раньше ради этого лист перечитывался
        целиком — полторы тысячи строк после каждой правки одной ячейки,
        и на слабой сети это выглядело как зависание. Теперь пересчитанная
        строка приезжает в ответе на саму правку.
      */
      const result = await sheetsApi.patchCells(sheetCode, [
        { rowId, data: { [key]: value } },
      ], options);
      options.ensureResult(result);
      markSaved(rowId, key);
      return true;
    } catch (e) {
      if (options.isCurrentWrite(rowId)) row.data = { ...row.data, [key]: before };
      markError(rowId, key);
      toasts.error(e instanceof ApiError ? e.message : 'Правка не сохранилась');
      if (strict) throw e;
      return false;
    } finally { options.release(); }
  }

  /**
   * Правка нескольких ячеек одной операцией.
   *
   * Так приходит вставка блока из Excel, протягивание вниз и очистка
   * диапазона. Возвращает прежние значения — по ним работает отмена:
   * вставка блока отменяется целиком, а не по ячейке.
   */
  async function applyCells(
    changes: Array<{ rowId: string; key: string; value: unknown }>,
    strict = false,
  ): Promise<Array<{ rowId: string; key: string; value: unknown }>> {
    if (observationActive.value) return [];
    if (!detail.value || !changes.length) return [];

    const byRow = new Map<string, Record<string, unknown>>();
    const rowById = new Map(rows.value.map((row) => [row.id, row]));
    const before: Array<{ rowId: string; key: string; value: unknown }> = [];

    for (const change of changes) {
      const row = rowById.get(change.rowId);
      if (!row && !strict) continue;
      before.push({
        rowId: change.rowId,
        key: change.key,
        value: row?.data[change.key] ?? null,
      });
      const patch = byRow.get(change.rowId) ?? {};
      patch[change.key] = change.value;
      byRow.set(change.rowId, patch);
    }

    // Значения показываем сразу: ответа ждут сотни ячеек, и мигание
    // «вставилось — пропало — появилось» читается как сбой
    mutationSequence += 1;
    for (const [rowId, patch] of byRow) {
      const row = rowById.get(rowId);
      if (!row) continue;
      row.data = { ...row.data, ...patch };
      for (const key of Object.keys(patch)) markSaving(rowId, key);
    }

    const sheetCode = detail.value.sheet.code;
    const options = dataResultOptions(sheetCode, [...byRow.keys()]);
    try {
      /*
        Один запрос на весь прямоугольник, а не запрос на строку в цикле.

        Вставка блока 20×5 давала двадцать запросов подряд, двадцать записей
        в журнале и — при вычисляемой колонке — двадцать полных перечитываний
        листа. Сервер пишет их одной пачкой под общим `batchId` и возвращает
        затронутые строки пересчитанными.
      */
      const result = await sheetsApi.patchCells(
        sheetCode,
        [...byRow].map(([rowId, data]) => ({ rowId, data })),
        options,
      );
      options.ensureResult(result);
      for (const change of changes) markSaved(change.rowId, change.key);
    } catch (e) {
      for (const change of changes) markError(change.rowId, change.key);
      toasts.error(e instanceof ApiError ? e.message : 'Правка не сохранилась');
      if (code.value === sheetCode) await reload({ background: true });
      if (strict) throw e;
      return [];
    } finally { options.release(); }

    return before;
  }

  /**
   * Подстановка пересчитанных строк на их места.
   *
   * Строка заменяется целиком, а не полем: вместе со значением приезжают
   * вычисленные колонки, подписи ссылок и оформление, и собирать их по
   * кусочкам значило бы держать здесь второе знание о том, из чего состоит
   * строка.
   */
  function applyFresh(fresh: SheetRow[]): void {
    if (!fresh.length) return;
    const byId = new Map(fresh.map((row) => [row.id, row]));
    rows.value = reconcileSheetRows(rows.value, rows.value.map((row) => byId.get(row.id) ?? row));
  }

  /**
   * Заливка нескольких ячеек одной операцией.
   *
   * Возвращает прежние цвета — по ним работает отмена. Пока заливка шла
   * запросом на ячейку, покраска прямоугольника 20×5 давала двадцать шагов
   * отмены: человек красил кусок, жал Ctrl+Z и получал обратно одну ячейку.
   */
  function formattingStillApplied<T>(
    sheetCode: string,
    patches: ReadonlyMap<string, Readonly<Record<string, T>>>,
    matches: (row: SheetRow, key: string, value: T) => boolean,
  ): boolean {
    if (code.value !== sheetCode) return false;
    // Нужны текущие строки: завершившийся GET заменяет объекты старого Map.
    const currentRows = new Map(rows.value.map((row) => [row.id, row]));
    return [...patches].every(([rowId, patch]) => {
      const row = currentRows.get(rowId);
      return !!row && Object.entries(patch).every(([key, value]) => matches(row, key, value));
    });
  }

  async function applyFills(
    cells: Array<{ rowId: string; key: string; fill: string | null }>,
    strict = false,
  ): Promise<Array<{ rowId: string; key: string; fill: string | null }>> {
    if (observationActive.value) return [];
    if (!detail.value || !cells.length) return [];
    const sheetCode = code.value;

    const byRow = new Map<string, Record<string, string | null>>();
    const rowById = new Map(rows.value.map((row) => [row.id, row]));
    const before: Array<{ rowId: string; key: string; fill: string | null }> = [];

    for (const cell of cells) {
      const row = rowById.get(cell.rowId);
      if (!row && !strict) continue;
      before.push({
        rowId: cell.rowId,
        key: cell.key,
        fill: row?.fills[cell.key] ?? null,
      });
      const patch = byRow.get(cell.rowId) ?? {};
      patch[cell.key] = cell.fill;
      byRow.set(cell.rowId, patch);
    }
    if (!byRow.size) return [];
    mutationSequence += 1;

    // Цвет показывается сразу: ответа ждут сотни ячеек
    for (const [rowId, patch] of byRow) {
      const row = rowById.get(rowId);
      if (!row) continue;
      const next = { ...row.fills };
      for (const [key, value] of Object.entries(patch)) {
        if (value) next[key] = value;
        else delete next[key];
      }
      row.fills = next;
    }

    try {
      await sheetsApi.patchCells(
        sheetCode,
        [...byRow].map(([rowId, fills]) => ({ rowId, fills })),
        { isApplied: () => formattingStillApplied(sheetCode, byRow,
          (row, key, value) => (row.fills[key] ?? null) === value) },
      );
    } catch (e) {
      toasts.error(e instanceof ApiError ? e.message : 'Заливка не сохранилась');
      if (code.value === sheetCode) await reload({ background: true });
      if (strict) throw e;
      return [];
    }

    return before;
  }

  /** Оформление нескольких ячеек одной операцией: те же правила, что у заливки. */
  async function applyStyles(
    cells: Array<{ rowId: string; key: string; style: SheetCellStyle | null }>,
    strict = false,
  ): Promise<Array<{ rowId: string; key: string; style: SheetCellStyle | null }>> {
    if (observationActive.value) return [];
    if (!detail.value || !cells.length) return [];
    const sheetCode = code.value;

    const byRow = new Map<string, Record<string, SheetCellStyle | null>>();
    const rowById = new Map(rows.value.map((row) => [row.id, row]));
    const before: Array<{
      rowId: string;
      key: string;
      style: SheetCellStyle | null;
    }> = [];

    for (const cell of cells) {
      const row = rowById.get(cell.rowId);
      if (!row && !strict) continue;
      before.push({
        rowId: cell.rowId,
        key: cell.key,
        style: row?.styles?.[cell.key] ?? null,
      });
      const patch = byRow.get(cell.rowId) ?? {};
      // Пустой объект на сервере снимает оформление. Копия также защищает
      // проверку WS-эха от последующего изменения объекта вызывающей стороной.
      const style = cell.style
        ? Object.fromEntries(Object.entries(cell.style).filter(([, value]) => value !== undefined)) as SheetCellStyle
        : null;
      patch[cell.key] = style && Object.keys(style).length ? style : null;
      byRow.set(cell.rowId, patch);
    }
    if (!byRow.size) return [];
    mutationSequence += 1;

    for (const [rowId, patch] of byRow) {
      const row = rowById.get(rowId);
      if (!row) continue;
      const next = { ...(row.styles ?? {}) };
      for (const [key, value] of Object.entries(patch)) {
        if (value) next[key] = value;
        else delete next[key];
      }
      row.styles = next;
    }

    try {
      await sheetsApi.patchCells(
        sheetCode,
        [...byRow].map(([rowId, styles]) => ({ rowId, styles })),
        { isApplied: () => formattingStillApplied(sheetCode, byRow, (row, key, value) => {
          const current = row.styles?.[key];
          if (!value) return !current || !Object.keys(current).length;
          return !!current && Object.keys(current).length === Object.keys(value).length &&
            Object.entries(value).every(([property, expected]) => current[property as keyof SheetCellStyle] === expected);
        }) },
      );
    } catch (e) {
      toasts.error(e instanceof ApiError ? e.message : 'Оформление не сохранилось');
      if (code.value === sheetCode) await reload({ background: true });
      if (strict) throw e;
      return [];
    }

    return before;
  }

  /**
   * Новая строка на своё место.
   *
   * Порядок — среднее между соседями: строка встаёт между ними, а весь
   * хвост листа не переписывается. Секцию берём у соседа сверху, иначе
   * строка, вставленная внутрь «МОСКВА и МО», уехала бы из секции.
   */
  async function insertRow(anchorId: string, where: 'above' | 'below'): Promise<void> {
    if (observationActive.value) return;
    if (!detail.value) return;
    const anchor = rows.value.find((row) => row.id === anchorId);
    const list = rows.value
      .filter((row) => row.sectionId === anchor?.sectionId)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    const index = list.findIndex((row) => row.id === anchorId);
    if (!anchor) return void addRow();

    const neighbour = where === 'above' ? list[index - 1] : list[index + 1];
    const low =
      where === 'above' ? (neighbour?.sortOrder ?? anchor.sortOrder - 20) : anchor.sortOrder;
    const high =
      where === 'above' ? anchor.sortOrder : (neighbour?.sortOrder ?? anchor.sortOrder + 20);

    try {
      const sheetCode = detail.value.sheet.code;
      const recordUndo = useUndoStack(sheetUndoContext(sheetCode)).capture();
      const created = await sheetsApi.createRow(sheetCode, {}, anchor.sectionId, (low + high) / 2);
      if (code.value === sheetCode) await reload({ background: true });
      recordAddedRow(sheetCode, created.id, recordUndo);
    } catch (e) {
      toasts.error(e instanceof ApiError ? e.message : 'Строка не добавилась');
    }
  }

  /**
   * Дописать строки в конец листа и вернуть их идентификаторы.
   *
   * Нужно вставке из буфера: блок из Excel обычно длиннее того, что есть
   * на листе, и в книге строки при вставке добавляются сами. Молча терять
   * хвост нельзя — это ровно те данные, ради которых блок и копировали.
   */
  async function appendRows(count: number): Promise<string[]> {
    if (observationActive.value) return [];
    if (!detail.value || count <= 0) return [];
    const sheetCode = detail.value.sheet.code;
    const last = rows.value.reduce<SheetRow | undefined>(
      (found, row) => (!found || row.sortOrder > found.sortOrder ? row : found),
      undefined,
    );
    const ids: string[] = [];

    try {
      for (let i = 0; i < count; i += 1) {
        const created = await sheetsApi.createRow(sheetCode, {}, last?.sectionId ?? null);
        ids.push(created.id);
      }
      if (code.value === sheetCode) await reload({ background: true });
      return ids;
    } catch (e) {
      toasts.error(e instanceof ApiError ? e.message : 'Строки не добавились');
      if (code.value === sheetCode) await reload({ background: true });
      return ids;
    }
  }

  async function deleteRows(ids: string[], strict = false): Promise<string[]> {
    if (observationActive.value) return [];
    if (!detail.value || !ids.length) return [];
    const sheetCode = detail.value.sheet.code;
    const deleted: string[] = [];
    try {
      for (const id of ids) {
        await sheetsApi.deleteRow(sheetCode, id);
        deleted.push(id);
      }
    } catch (e) {
      toasts.error(e instanceof ApiError ? e.message : 'Строки не удалились');
      if (strict) throw e;
    } finally {
      if (code.value === sheetCode) {
        rows.value = rows.value.filter((row) => !deleted.includes(row.id));
        total.value = Math.max(0, total.value - deleted.length);
      }
    }
    return deleted;
  }

  async function restoreRows(ids: string[], strict = false): Promise<void> {
    if (observationActive.value) return;
    if (!detail.value || !ids.length) return;
    const sheetCode = detail.value.sheet.code;
    try {
      for (const id of ids) await sheetsApi.restoreRow(sheetCode, id);
      if (code.value === sheetCode) await reload({ background: true });
    } catch (e) {
      toasts.error(e instanceof ApiError ? e.message : 'Строки не вернулись');
      if (strict) throw e;
    }
  }

  async function setFill(rowId: string, key: string, color: string | null): Promise<void> {
    if (observationActive.value) return;
    if (!detail.value) return;
    const row = rows.value.find((r) => r.id === rowId);
    if (!row) return;
    const before = { ...row.fills };
    const next = { ...row.fills };
    if (color) next[key] = color;
    else delete next[key];
    row.fills = next;

    try {
      await sheetsApi.patchRow(detail.value.sheet.code, rowId, {
        fills: { [key]: color },
      });
    } catch (e) {
      row.fills = before;
      toasts.error(e instanceof ApiError ? e.message : 'Заливка не сохранилась');
    }
  }

  function recordAddedRow(sheetCode: string, id: string, recordUndo: UndoRecorder): void {
    recordUndo({
      label: 'Добавление строки',
      undo: async () => {
        await sheetsApi.deleteRow(sheetCode, id);
        if (code.value === sheetCode) await reload({ background: true });
      },
      redo: async () => {
        await sheetsApi.restoreRow(sheetCode, id);
        if (code.value === sheetCode) await reload({ background: true });
      },
    });
  }

  async function addRow(sectionId: string | null = null): Promise<string | undefined> {
    if (observationActive.value) return;
    if (!detail.value) return;
    try {
      const sheetCode = detail.value.sheet.code;
      const recordUndo = useUndoStack(sheetUndoContext(sheetCode)).capture();
      const created = await sheetsApi.createRow(sheetCode, {}, sectionId);
      if (code.value === sheetCode) await reload({ background: true });
      recordAddedRow(sheetCode, created.id, recordUndo);
      return created.id;
    } catch (e) {
      toasts.error(e instanceof ApiError ? e.message : 'Строка не добавилась');
    }
  }

  /** Форма базы проверяет дубли до создания и сохраняет отдельную историю листа. */
  async function createBaseCandidate(data: SheetRowData, ignoreDuplicates = false): Promise<string> {
    if (code.value !== 'base' || !canEdit.value || !detail.value?.sheet.canAddRows) {
      throw new Error('Нет права добавлять кандидатов в базу');
    }
    const recordUndo = useUndoStack(sheetUndoContext('base')).capture();
    const created = await sheetsApi.createBaseCandidate(data, ignoreDuplicates);
    if (code.value === 'base') await reload({ background: true });
    recordAddedRow('base', created.id, recordUndo);
    return created.id;
  }

  async function removeRow(rowId: string): Promise<void> {
    if (observationActive.value) return;
    if (!detail.value) return;
    try {
      await sheetsApi.deleteRow(detail.value.sheet.code, rowId);
      rows.value = rows.value.filter((r) => r.id !== rowId);
      total.value = Math.max(0, total.value - 1);
    } catch (e) {
      toasts.error(e instanceof ApiError ? e.message : 'Строка не удалилась');
    }
  }

  function setVisible(keys: string[]): void {
    visibleKeys.value = keys;
    persistLayout();
  }

  function setPinned(key: string, value: 'left' | null, sheetCode = code.value): void {
    if (sheetCode !== code.value) {
      const layout = readLayout(sheetCode);
      if (layout) {
        layout.pinned = { ...layout.pinned, [key]: value };
        try {
          localStorage.setItem(layoutKey(sheetCode), JSON.stringify(layout));
        } catch {
          /* Личная раскладка недоступна. Общее закрепление сохранено сервером. */
        }
      }
      return;
    }
    personalPins.value = { ...personalPins.value, [key]: value };
    persistLayout();
  }

  function setWidth(key: string, width: number): void {
    widths.value = { ...widths.value, [key]: width };
    persistLayout();
  }

  /** Сортировка из меню колонки: направление задано явно. */
  function setSort(key: string, dir: 'asc' | 'desc' | null): void {
    sort.value = dir ? [{ field: key, dir }] : [];
    void reload();
  }

  /**
   * Ширина колонки после перетаскивания края.
   *
   * У того, кто распоряжается листом, ширина общая: в книге её тянут
   * для всех, и «у меня узко, а у соседа широко» читается как поломка.
   * Остальным ширина остаётся личной — в браузере.
   */
  async function commitWidth(key: string, width: number): Promise<void> {
    if (observationActive.value) return;
    setWidth(key, width);
    if (!canManage.value || !detail.value) return;
    const column = detail.value.columns.find((c) => c.key === key);
    if (!column) return;
    try {
      await sheetsApi.updateColumn(detail.value.sheet.code, column.id, {
        width,
      });
      column.width = width;
    } catch {
      // Не сохранилось — ширина всё равно применена локально
    }
  }

  function toggleSort(key: string): void {
    const current = sort.value[0];
    if (!current || current.field !== key) sort.value = [{ field: key, dir: 'asc' }];
    else if (current.dir === 'asc') sort.value = [{ field: key, dir: 'desc' }];
    // Третье нажатие снимает сортировку и возвращает порядок листа —
    // в книге он смысловой: объекты идут не по алфавиту
    else sort.value = [];
    void reload();
  }

  function persistLayout(): void {
    if (!code.value || temporaryObservationLayout.value) return;
    try {
      localStorage.setItem(
        layoutKey(code.value),
        JSON.stringify({
          ...(code.value === 'rotation' ? { rotationStatusLayoutVersion: 1 } : {}),
          ...(code.value === 'base' ? {
            basePhoneLayoutVersion: BASE_PHONE_LAYOUT_VERSION,
            baseRowNumberLayoutVersion: BASE_ROW_NUMBER_LAYOUT_VERSION,
            baseAgeLayoutVersion: BASE_AGE_LAYOUT_VERSION,
          } : {}),
          columns: visibleColumns.value.map((c) => c.key),
          widths: widths.value,
          pinned: personalPins.value,
        }),
      );
    } catch {
      // Хранилище закрыто политикой браузера — раскладка живёт до перезагрузки
    }
  }

  async function refreshDetail(): Promise<void> {
    if (!detail.value) return;
    invalidateRows();
    const sheetCode = code.value;
    const request = openSequence;
    let fresh: SheetDetail;
    try {
      fresh = await sheetsApi.detail(sheetCode);
    } catch (error) {
      if (request === openSequence && code.value === sheetCode
        && error instanceof ApiError && [401, 403, 404].includes(error.status)) {
        closeUnavailableSheet(error.message);
      }
      throw error;
    }
    if (code.value !== sheetCode || request !== openSequence || !detail.value) return;
    // Новые счётчики секций не пересоздают неизменившиеся колонки и их обмер.
    for (const key of Object.keys(fresh) as Array<keyof SheetDetail>) {
      if (JSON.stringify(detail.value[key]) !== JSON.stringify(fresh[key])) {
        Object.assign(detail.value, { [key]: fresh[key] });
      }
    }
  }

  /** Компенсации адресованы исходному листу даже между несколькими await. */
  function historyActionsFor(sheetCode: string) {
    const refresh = async () => {
      if (code.value === sheetCode) await reload({ background: true });
    };
    const patch = async (changes: Parameters<typeof sheetsApi.patchCells>[1]) => {
      await sheetsApi.patchCells(sheetCode, changes);
      await refresh();
    };
    function combine<T>(
      cells: Array<{ rowId: string; key: string } & T>,
      read: (cell: T) => unknown,
    ) {
      const byRow = new Map<string, Record<string, unknown>>();
      for (const cell of cells) {
        const data = byRow.get(cell.rowId) ?? {};
        data[cell.key] = read(cell);
        byRow.set(cell.rowId, data);
      }
      return [...byRow];
    }
    return {
      applyCells: async (cells: Array<{ rowId: string; key: string; value: unknown }>) => {
        if (code.value === sheetCode && !observationActive.value) {
          await applyCells(cells, true);
          return;
        }
        await patch(
          combine(cells, (cell) => cell.value).map(([rowId, data]) => ({
            rowId,
            data,
          })),
        );
      },
      applyFills: async (cells: Array<{ rowId: string; key: string; fill: string | null }>) => {
        if (code.value === sheetCode && !observationActive.value) {
          await applyFills(cells, true);
          return;
        }
        await patch(
          combine(cells, (cell) => cell.fill).map(([rowId, fills]) => ({
            rowId,
            fills: fills as Record<string, string | null>,
          })),
        );
      },
      applyStyles: async (
        cells: Array<{
          rowId: string;
          key: string;
          style: SheetCellStyle | null;
        }>,
      ) => {
        if (code.value === sheetCode && !observationActive.value) {
          await applyStyles(cells, true);
          return;
        }
        await patch(
          combine(cells, (cell) => cell.style).map(([rowId, styles]) => ({
            rowId,
            styles: styles as Record<string, SheetCellStyle | null>,
          })),
        );
      },
      deleteRows: async (ids: string[]) => {
        for (const id of ids) await sheetsApi.deleteRow(sheetCode, id);
        await refresh();
      },
      restoreRows: async (ids: string[]) => {
        for (const id of ids) await sheetsApi.restoreRow(sheetCode, id);
        await refresh();
      },
    };
  }

  return {
    sheets,
    detail,
    rows,
    rowsRevision,
    rowsCode,
    visibleRows,
    columnFilters,
    distinctValues,
    setColumnFilter,
    clearColumnFilters,
    total,
    today,
    loading,
    hasSnapshot,
    hasLoadedRows,
    invalidateRows,
    enter,
    refreshing,
    refreshError,
    error,
    search,
    sort,
    activeViewId,
    activeView,
    activeFilterCount,
    code,
    columns,
    visibleColumns,
    canEdit,
    canManage,
    widthOf,
    loadList,
    open,
    reload,
    selectView,
    resetFilters,
    setValue,
    setFill,
    addRow,
    createBaseCandidate,
    removeRow,
    applyCells,
    applyRowPositions,
    applyFills,
    applyStyles,
    appendRows,
    widths,
    insertRow,
    deleteRows,
    restoreRows,
    setVisible,
    setWidth,
    setPinned,
    commitWidth,
    setSort,
    toggleSort,
    refreshDetail,
    historyActionsFor,
    captureObservationView,
    discardObservation,
    observationSession: computed(() => sessionSequence.value),
    applyObservationView,
    temporaryObservationLayout,
    observationActive,
  };
}

// Отдельный экземпляр для компонентов, смонтированных вне рабочей книги.
function sheetStoreDefinition(id: string, code?: string) {
  return defineStore(id, () => createSheetState(code));
}
const useStandaloneSheetStore = sheetStoreDefinition('sheets');
type SheetStore = ReturnType<typeof useStandaloneSheetStore>;
const sessionStores = new WeakMap<Pinia, Map<string, SheetStore>>();
/** Realtime не создаёт стор и реактивные подписки для ещё не открытого листа. */
export function findSheetStore(code: string): SheetStore | undefined {
  const pinia = getActivePinia();
  return pinia ? sessionStores.get(pinia)?.get(code) : undefined;
}
export function invalidateSheetSnapshots(): void {
  const pinia = getActivePinia();
  if (pinia) sessionStores.get(pinia)?.forEach(store => store.invalidateRows());
}
const sheetContext: InjectionKey<SheetStore> = Symbol('sheetStore');

/** Явный код — для маршрута/сервисов; дочерние компоненты получают стор своего листа. */
export function useSheetsStore(sheetCode?: string): SheetStore {
  if (sheetCode !== undefined) {
    const store = sheetStoreDefinition(`sheet:${sheetCode}`, sheetCode)();
    const pinia = getActivePinia()!;
    let stores = sessionStores.get(pinia);
    if (!stores) sessionStores.set(pinia, stores = new Map());
    stores.set(sheetCode, store);
    return store;
  }
  const provided = hasInjectionContext() ? inject(sheetContext, null) : null;
  return provided ?? useStandaloneSheetStore();
}

export function provideSheetStore(sheetCode: string): SheetStore {
  const store = useSheetsStore(sheetCode);
  provide(sheetContext, store);
  return store;
}

export type { SheetRowData };
